<template>
  <div class="avatar-builder-ui" :style="avatarBuilderUIStyle">
    <div class="avatar-builder-preview-area">
      <PreviewBackground />
      <keep-alive v-if="this.$store.getters.is3dMode">
        <AvatarBuilder3DMainPreview
          :bottomWrapDown="bottomWrapDown"
          :is-full-body-category="isFullBodyCategory"
        />
      </keep-alive>
      <AvatarBuilderMainPreview v-else :is-full-body-category="isFullBodyCategory" />

      <SelfiePreview v-if="showSelfiePreview" :is-full-body-category="isFullBodyCategory" />

      <ColourPicker
        v-if="!!colourPickerConfigForSelectedItem"
        :colourPickerConfig="colourPickerConfigForSelectedItem"
        :isOutfitSharingButtonVisible="showOutfitSharingButton"
      />

      <TuckToggle
        v-if="shouldShowTuckToggle"
        @on-toggle-change="setTuckStateForItem"
        :tuckState="this.$store.getters.topToggleTuckState"
        :isDisabled="this.$store.getters.isTopToggleDisabled"
      />

      <UndoRedoButton />
      <OutfitSharingButton v-if="showOutfitSharingButton" />
    </div>

    <div class="back-button" @click="onExitTap" v-show="builderIsFocused">
      <img
        class="svg-img"
        src="@/assets/avbuilder_cancel.svg"
        title="exit_button"
        draggable="false"
      />
    </div>
    <div class="save-button" @click="onSaveTap" v-show="builderIsFocused">
      <span v-localize="SAVE_AVATAR_BUTTON_TEXT_KEY" />
    </div>

    <div
      :class="{
        'avatar-builder-bottom-half-wrapper': true,
        'avatar-builder-bottom-half-wrapper-swipe': bottomWrapDown,
        makeup: isMakeupCategory,
      }"
      ref="bottomWrap"
      v-if="showUpdatedTraitNavBar"
    >
      <div class="category-title-updated" ref="bottom">
        <div class="title" v-localize="selectedCategory" />
      </div>

      <div class="bottombar-container-updated">
        <BottomBar :showGradient="!isIOS12" :isMakeup="isMakeupCategory" />
      </div>

      <div class="avatar-builder-category-container-updated">
        <div class="category-slider" data-snappable-pager="horizontal">
          <AvatarBuilderCategory
            class="category"
            v-for="(category, index) in categories"
            :key="category.key"
            :category-index="index"
            :category="category"
            :showGradient="true"
            @scroll="onScrollToBottom"
          />
        </div>
        <div :class="bottomGradientStyle" />
      </div>
    </div>

    <div
      :class="{
        'avatar-builder-bottom-half-wrapper': true,
        'avatar-builder-bottom-half-wrapper-swipe': bottomWrapDown,
        makeup: isMakeupCategory,
      }"
      ref="bottomWrap"
      v-else
    >
      <div class="category-title" ref="bottom">
        <div class="category-arrow" @click="SnappablePager_goToPrevious" id="arrow_btn_back">
          <img
            v-if="shouldShowPrevArrow"
            src="@/assets/attribute_prev_arrow@3x.png"
            draggable="false"
          />
        </div>
        <div class="title" v-localize="selectedCategory" />
        <div class="category-arrow" @click="SnappablePager_goToNext" id="arrow_btn_forward">
          <img
            v-if="shouldShowNextArrow"
            src="@/assets/attribute_next_arrow@3x.png"
            draggable="false"
          />
        </div>
      </div>

      <div class="avatar-builder-category-container">
        <div class="category-slider" data-snappable-pager="horizontal">
          <AvatarBuilderCategory
            class="category"
            v-for="(category, index) in categories"
            :key="category.key"
            :category-index="index"
            :category="category"
          />
        </div>
      </div>

      <div class="bottombar-container">
        <BottomBar />
      </div>
    </div>
  </div>
</template>

<script>
import { captureMessage } from '@sentry/vue';
import AvatarBuilderCategory from '@/components/AvatarBuilderUI/AvatarBuilderCategory.vue';
import AvatarBuilderMainPreview from '@/components/AvatarBuilderUI/AvatarBuilderMainPreview.vue';
// import AvatarBuilder3DMainPreview from '@/components/AvatarBuilderUI/AvatarBuilder3DMainPreview.vue';
import BottomBar from '@/components/AvatarBuilderUI/BottomBar.vue';
import ColourPicker from '@/components/AvatarBuilderUI/ColourPicker.vue';
import TuckToggle from '@/components/AvatarBuilderUI/Fashion/TuckToggle.vue';
import PreviewBackground from '@/components/AvatarBuilderUI/PreviewBackground.vue';
import UndoRedoButton from '@/components/AvatarBuilderUI/UndoRedoButton.vue';
import OutfitSharingButton from '@/components/AvatarBuilderUI/OutfitSharingButton.vue';
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import TraitsHelper from '@/services/TraitsHelper';
import SelfiePreview from '@/components/AvatarBuilderUI/SelfiePreview.vue';
import SnappablePagerMixin from '@/mixins/SnappablePagerMixin';
import CategoryAssets from '@/services/CategoryAssets';
import { OUTFIT_CATEGORY_KEY } from '@/constants';
import { FlowModeEnum } from '@/enum/flow_mode';
import { MixAndMatchCategoryEnum, MixAndMatchTuckStateEnum } from '@/enum/mix_and_match';
import { LocalizationDirective } from '@/directives/LocalizationDirective';
import ClientBuild from '@/services/ClientBuild';
import FeatureGating from '@/services/FeatureGating';
import DebouncedHandler from '@/services/DebouncedHandler';
import ReceiveClientMessage from '@/services/ReceiveClientMessage';
import { clientPlatform, ClientPlatformEnum } from '@/services/ClientPlatform';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';

const MAKEUP_CATEGORIES = ['lipstick_tone', 'eyeshadow_tone', 'blush_tone'];

export default {
  name: 'AvatarBuilderUI',
  data: function() {
    return {
      atScrollEnd: false,
    };
  },
  components: {
    AvatarBuilderCategory,
    AvatarBuilder3DMainPreview: () =>
      import(
        /* webpackChunkName: "3d" */
        /* webpackMode: "lazy" */
        '@/components/AvatarBuilderUI/AvatarBuilder3DMainPreview.vue'
      ),
    AvatarBuilderMainPreview,
    BottomBar,
    ColourPicker,
    TuckToggle,
    OutfitSharingButton,
    UndoRedoButton,
    SelfiePreview,
    PreviewBackground,
  },
  mixins: [SnappablePagerMixin],
  computed: {
    isIOS12() {
      return this.$store.getters.isIOS12;
    },
    showSelfiePreview() {
      return (
        this.$store.getters.flowMode === FlowModeEnum.CREATE &&
        FeatureGating.isSelfiePreviewEnabled()
      );
    },
    showUpdatedTraitNavBar() {
      return FeatureGating.isUpdatedTraitNavEnabled();
    },
    flowModeIsProfile() {
      return this.$store.getters.flowMode === FlowModeEnum.PROFILE;
    },
    builderIsFocused() {
      return (
        !this.flowModeIsProfile || (this.flowModeIsProfile && this.$store.getters.builderIsFocused)
      );
    },
    categories() {
      return this.$store.getters.categories;
    },
    selectedCategory() {
      return this.$store.getters.selectedCategory;
    },
    shouldShowPrevArrow() {
      return this.$store.getters.selectedCategoryIndex > 0;
    },
    shouldShowNextArrow() {
      return this.$store.getters.selectedCategoryIndex < this.$store.getters.categories.length - 1;
    },
    colourPickerConfigForSelectedItem() {
      // Notably, the colour pickers for facial features, mix and match, and traits can all be passed here.
      // Though they take completely different types of data the way they are displayed is exactly the same.
      const colourPickerConfig =
        this.colourPickerConfigForSelectedFashionItem ||
        this.colourPickerConfigForSelectedFacialFeature ||
        this.colourPickerConfigForSelectedTrait;

      // Don't show the colour picker if there are not >1 colour picker options for the selected item.
      if (
        !colourPickerConfig ||
        !colourPickerConfig.options ||
        colourPickerConfig.options.length <= 1
      ) {
        return null;
      }
      return colourPickerConfig;
    },
    colourPickerConfigForSelectedFashionItem() {
      const selectedCategory = this.selectedCategory;
      const clothingCategory = MixAndMatchHelper.getItemCategory(
        this.$store.getters.selectedCategory,
      );
      return MixAndMatchHelper.colourPickerConfigForSelectedItem(
        selectedCategory,
        this.$store.getters.traits[clothingCategory],
        this.$store.getters.categories,
        this.$store.getters.mixAndMatchColourGroups,
      );
    },
    colourPickerConfigForSelectedFacialFeature() {
      return FacialFeaturesHelper.colourPickerConfigForSelectedItem(
        this.$store.getters.facialFeaturesMetadata,
        this.selectedCategory,
        this.$store.getters.facialFeatureTraits,
      );
    },
    colourPickerConfigForSelectedTrait() {
      const selectedCategory = this.$store.getters.selectedCategory;
      return TraitsHelper.colourPickerConfigForSelectedItem(
        selectedCategory,
        this.$store.getters.traits[selectedCategory],
        this.$store.getters.categories,
        this.$store.getters.categoryMetadata.swatch_sets,
      );
    },
    shouldShowTuckToggle() {
      return (
        this.$store.getters.selectedCategory === MixAndMatchCategoryEnum.TOP ||
        this.$store.getters.selectedCategory === MixAndMatchCategoryEnum.BOTTOM
      );
    },
    avatarBuilderUIStyle() {
      return {
        height: `calc(100% - ${ClientBuild.getClientInsets().top}px)`,
        marginTop: `${ClientBuild.getClientInsets().top}px`,
      };
    },
    isMakeupCategory() {
      return MAKEUP_CATEGORIES.some((category) => this.selectedCategory === category);
    },
    isFullBodyCategory: function() {
      return this.$store.getters.isFullBodyCategory;
    },
    bottomWrapDown: function() {
      return this.$store.getters.bottomWrapDown && this.flowModeIsProfile;
    },
    bottomGradientStyle() {
      if (this.atScrollEnd) {
        return ['position-gradient', 'bottom-gradient'];
      } else {
        return ['position-gradient', 'bottom-gradient', 'not-scroll-end'];
      }
    },
    isWeb() {
      return clientPlatform === ClientPlatformEnum.Web;
    },
    isCreateMode() {
      return this.$store.getters.flowMode === FlowModeEnum.CREATE;
    },
    isFashionOrClosetCategory() {
      return (
        this.$store.getters.selectedCategory == OUTFIT_CATEGORY_KEY ||
        DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(this.$store.getters.selectedCategory) ||
        this.$store.getters.selectedCategory == 'my_closet'
      );
    },
    showOutfitSharingButton() {
      return (
        FeatureGating.shareOutfitCTAOption() &&
        !this.isWeb &&
        !this.isCreateMode &&
        this.isFashionOrClosetCategory
      );
    },
  },
  created: function() {
    this.SAVE_AVATAR_BUTTON_TEXT_KEY = 'AvatarSaveButtonTitle';
    this.$store.dispatch('loadAssetsData');
    this.$store.dispatch('loadPromotionsData');
  },
  mounted: function() {
    // Pop up an error modal to users whose OS doesn't support `flex`.
    // That prevents Android < 4.4 from accessing Web Builder.
    if (!global.CSS.supports('display', 'flex')) {
      this.$store.commit('setShouldShowUnsupportedOSError', { show: true });
      captureMessage('Unsupported OS version', {
        extra: { message: `'transform' is unsupported` },
      });
      return;
    }
    this.touchStartY = 0;
    this.touchEndY = 0;

    if (this.flowModeIsProfile) {
      ReceiveClientMessage.BuilderIsFocusedBus.$on('set', (isFocused) => {
        this.$store.commit('setBuilderIsFocused', { isFocused });
      });
      this.$refs.bottom.addEventListener('touchstart', (e) => {
        this.touchStartY = e.changedTouches[0].screenY;
        this.$refs.bottomWrap.style.transition = '0s all';
      });
      this.$refs.bottom.addEventListener('touchmove', (e) => {
        this.touchEndY = e.changedTouches[0].screenY;

        const bottomWrapShowing = !this.bottomWrapDown;

        const diff = this.touchEndY - this.touchStartY;
        if (bottomWrapShowing) {
          this.$refs.bottomWrap.style.transform = `translateY(clamp(0%, calc(${diff}px), 91%))`;
        } else if (!bottomWrapShowing)
          this.$refs.bottomWrap.style.transform = `translateY(clamp(0%, calc(91% + ${diff}px), 91%))`;
      });
      this.$refs.bottom.addEventListener('touchcancel', () => {
        this.$refs.bottomWrap.style.transition = null;
        this.$refs.bottomWrap.style.transform = null;
      });
      this.$refs.bottom.addEventListener('touchend', (e) => {
        this.$refs.bottomWrap.style.transition = null;
        this.$refs.bottomWrap.style.transform = null;

        this.touchEndY = e.changedTouches[0].screenY;
        if (this.touchEndY - this.touchStartY > 60) {
          this.$store.commit('setBottomWrapDown', { bottomWrapDown: true });
        } else if (this.touchEndY - this.touchStartY < -60) {
          this.$store.commit('setBottomWrapDown', { bottomWrapDown: false });
        }
      });

      // There appears to be a css bug when there is no delay on the bottom wrapper animation.
      // When there is no delay, the entire avatar preview gets pushed up. It doesn't make sense since
      //  there is no css logic that holds it up. A delay seems to fix this issue. Maybe a problem
      //  with how css and state are updated by Vue and DOM?
      setTimeout(() => {
        this.$store.commit('setBottomWrapDown', { bottomWrapDown: true });
      }, 500);
    }

    window.addEventListener('resize', this.scrollToCurrentCategory);

    // The Avatar Builder might not start off with the first category.
    // For example, Edit flow begins with the Hair category.
    // Therefore, we need to update the scrolling position manually
    // at initialization - only once for the app lifetime.
    this.scrollToCurrentCategory();
  },
  methods: {
    onScrollToBottom(atScrollEnd) {
      this.atScrollEnd = atScrollEnd;
    },
    onExitTap: DebouncedHandler(function() {
      // This method handles the builder exit button tap.
      this.$store.commit('setShouldShowExitDialogIfChanges', { show: true });
      if (!this.$store.getters.shouldShowExitDialog) {
        this.$store.dispatch('exitBuilder');
      }
    }),
    onSaveTap: DebouncedHandler(function() {
      const outfitCategoryIndex = CategoryAssets.getCategoryIndex(
        OUTFIT_CATEGORY_KEY,
        this.$store.getters.gender,
        this.$store.getters.style,
        this.$store.getters.flowMode,
      );
      if (
        this.$store.getters.traits &&
        MixAndMatchHelper.isDefault(this.$store.getters.traits) &&
        this.$store.getters.selectedCategoryIndex < outfitCategoryIndex
      ) {
        this.$store.commit('setShouldShowOutfitDialog', { show: true });
      } else {
        this.$store.dispatch('saveAvatar');
      }
    }),
    // Setting tuck state has to be set in AvatarBuilderUI (instead of in TuckToggle)
    // because in lower iOS versions, click events don't get called properly unless it's being called from the parent component
    setTuckStateForItem: function() {
      const initialTraits = Object.assign({}, this.$store.getters.traits);
      const newTuckState = this.$store.getters.topToggleTuckState ^ 1; // flip 1 -> 0, and vice versa
      const newTuckStateEnum = newTuckState
        ? MixAndMatchTuckStateEnum.TUCK
        : MixAndMatchTuckStateEnum.UNTUCK;
      this.$store.commit('setTopToggleTuckState', { tuckState: newTuckState });
      this.$store.commit('setTuckForMixAndMatchTop', {
        tuckState: newTuckStateEnum,
        isTopTuckable: true,
      });
      // top guaranteed to exist as it is a vital outfit key
      this.$store.commit('setTuckStateForTop', {
        tuckState: newTuckState,
        value: initialTraits[MixAndMatchCategoryEnum.TOP],
      });
      this.$store.commit('updateAvatarActions', {
        initialTraits,
      });
    },
  },
  directives: {
    LocalizationDirective,
  },
};
</script>

<style scoped>
.avatar-builder-preview-area {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.avatar-builder-bottom-half-wrapper {
  top: 48%; /* --top-half-height */
  display: flex;
  flex-direction: column;
  bottom: 0;
  position: absolute;
  width: 100%;
  transition: background-color 0.4s ease, transform 0.4s ease-out;
  border-radius: 15px 15px 0 0;
  box-shadow: 0px 0px 32px rgba(0, 0, 0, 0.24);
  background: #fff;
  color: #28292b;
  overflow: hidden;
}

.makeup {
  background: #21212a;
  --left-gradient: var(--left-makeup-gradient);
  --right-gradient: var(--right-makeup-gradient);
  --bottom-gradient: var(--bottom-makeup-gradient);
  color: #fff;
}

.avatar-builder-bottom-half-wrapper-swipe {
  transform: translateY(91%);
}

::-webkit-scrollbar {
  display: none;
  width: 0px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}

.avatar-builder-ui {
  /*
    Note: allow overflow to avoid clipping the preview background
    
    Support hardware acceleration:
    https://stackoverflow.com/a/18529444
   */
  -webkit-transform: translateZ(0); /* Safari */
  transform: translateZ(0);
  --left-gradient: linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0) 10%);
  --right-gradient: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 90%,
    rgba(255, 255, 255, 0.8) 100%
  );
  --bottom-gradient: linear-gradient(rgba(255, 255, 255, 0) 40%, rgba(255, 255, 255, 0.6) 100%);

  --left-dark-gradient: linear-gradient(90deg, rgba(18, 18, 18, 0.8) 0%, rgba(18, 18, 18, 0) 10%);
  --right-dark-gradient: linear-gradient(
    90deg,
    rgba(18, 18, 18, 0) 90%,
    rgba(18, 18, 18, 0.8) 100%
  );
  --bottom-dark-gradient: linear-gradient(rgba(18, 18, 18, 0) 40%, rgb(28, 28, 28, 0.6) 100%);

  --left-makeup-gradient: linear-gradient(90deg, rgba(33, 33, 42, 0.8) 0%, rgba(33, 33, 42, 0) 10%);
  --right-makeup-gradient: linear-gradient(
    90deg,
    rgba(33, 33, 42, 0) 90%,
    rgba(33, 33, 42, 0.8) 100%
  );
  --bottom-makeup-gradient: linear-gradient(rgba(33, 33, 42, 0) 40%, rgb(33, 33, 42, 0.6) 100%);
}

.avatar-builder-preview {
  height: 48%;
  position: relative;
  display: flex;
  align-items: center;
}

.avatar-builder-preview img {
  margin: 0 auto;
  object-fit: contain;
  max-height: 90%;
}

.category-title,
.category-title-updated {
  flex-basis: var(--category-title-height);
  width: 100%;
  display: flex;
  flex-direction: row;
  border-radius: 15px 15px 0 0;
}

.category-title .title {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  /* Any bigger than this causes issues https://jira.sc-corp.net/browse/BM-6164 */
  font-size: 14px;
  flex-basis: auto;
  flex-grow: 1;
  padding: 10px 0 0 16px;
}

.category-title-updated .title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: bold;
  /* Any bigger than this causes issues https://jira.sc-corp.net/browse/BM-6164 */
  font-size: 16px;
  flex-basis: auto;
  flex-grow: 1;
  padding: 10px 0 0 16px;
}

.category-slider {
  background: white;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  text-align: center;
}

.category-slider .category {
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 100%;
}

.category-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-basis: 10%;
}

.category-arrow img {
  cursor: pointer;
  max-width: 100%;
  max-height: 2vh; /* 40% of --category-title-height (5vh) */
  object-fit: contain;
}

.bottombar-container,
.bottombar-container-updated {
  flex-basis: var(--bottom-bar-height);
  width: 100%;
  position: relative;
}

.back-button {
  position: absolute;
  cursor: pointer;
  top: 16pt;
  left: 11pt;
  color: white;
  width: 20pt;
  height: 20pt;
}

.save-button {
  position: absolute;
  cursor: pointer;
  top: 16px;
  right: 8px;
  line-height: 36px;
  padding: 0 16px;
  background-color: #fff;
  border-radius: 18px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  font-weight: bold;
  letter-spacing: 0.6px;
  text-align: center;
  color: #16191c;
  -webkit-font-smoothing: antialiased;
  font-family: 'Avenir Next', 'Avenir', sans-serif;
}

.avatar-builder-category-container,
.avatar-builder-category-container-updated {
  flex-basis: auto;
  flex-grow: 1;
  width: 100%;
  display: flex;
}

.svg-img {
  width: 100%;
}

.position-gradient {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 25%;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.5s linear;
}

.bottom-gradient {
  background: var(--bottom-gradient);
  z-index: 1;
}

.not-scroll-end {
  opacity: 1;
  transition: opacity 0.5s linear;
}

@media (orientation: landscape) {
  .avatar-builder-ui:before {
    content: '';
    display: block;
    width: 100%;
    height: 123px;
    background: #fff url('../assets/logo.png') no-repeat 50% 22px/92px 27px;
  }
  .avatar-builder-bottom-half-wrapper {
    width: 40%;
    top: 123px;
    right: 0;
    box-shadow: none;
    background: none;
    color: #28292b;
  }
  .avatar-builder-preview-area {
    overflow: hidden;
    right: 40%;
    top: 123px;
    border: 16px solid transparent;
    border-radius: 36px;
  }
  .avatar-builder-preview-area:after {
    position: absolute;
    content: '';
    border-radius: 20px;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
    border: 1px solid transparent;
  }
  .bottombar-container {
    position: fixed;
    top: 123px;
    left: 0;
    transform: translateY(-100%);
    background: none !important;
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
  }
  .back-button img {
    filter: brightness(0.5);
  }
  .save-button {
    box-shadow: none;
    border: 2px solid rgba(0, 0, 0, 0.1);
    background-clip: padding-box;
    line-height: 32px;
    right: 16px;
    padding: 0 24px;
    transition: 0.25s;
  }
  .save-button:hover {
    color: #0a92ff;
  }
  .save-button:active {
    border-color: #0a92ff;
  }
}

@media (prefers-color-scheme: dark) {
  .avatar-builder-ui:before {
    -webkit-filter: invert();
    filter: invert();
  }
  .category-slider,
  .avatar-builder-bottom-half-wrapper,
  .category-title {
    background: #121212;
    color: #fff;
  }
  .avatar-builder-preview-area:after {
    border-color: #404040;
  }
  .category-arrow img {
    /* Brighten the arrows in dark mode for better visibility */
    -webkit-filter: brightness(1.2);
    filter: brightness(1.2);
  }
  .bottombar-container {
    background: #141414;
    -webkit-box-shadow: inset 0px 0.66px 0px #2c2c2d;
    box-shadow: inset 0px 0.66px 0px #2c2c2d;
  }
  .back-button img {
    filter: none;
  }
  .bottom-gradient {
    background: var(--bottom-dark-gradient);
  }
}
</style>
